import type {
	FoxItAnnot,
	FoxItComponent,
	FoxItField,
	FoxItPDFForm,
} from '@/FoxItTypes'
import {
	getNormalizedSignatureFieldName,
	setSignatureDateField,
	setSignatureRelatedTextField,
} from '@/components/documents/Foxit/FoxItHelpers'
import { useFoxitInstance } from '@/components/documents/Foxit/composables/FoxitInstance'
import { DocumentType } from '@/enums/DocumentType'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { PDFViewCtrl } from '@foxitsoftware/foxit-pdf-sdk-for-web-library'
import { ref } from 'vue'

const activeSignature = ref<string | null>(null)
const { getPdfViewer, getPdfui } = useFoxitInstance()

export const useFoxitUI = () => {
	const sessionDocumentsStore = useSessionDocumentsStore()
	const sessionStore = useSessionStore()

	// Clears the active signature
	const clearActiveSignature = () => {
		activeSignature.value = null
	}

	const updateAutofillButton = () => {
		if (sessionDocumentsStore.currentStep?.completed) {
			document.querySelector('#autofill-btn')?.classList.add('hidden')
		} else {
			document.querySelector('#autofill-btn')?.classList.remove('hidden')
		}
	}

	const updateChangePaymentTypeButton = () => {
		if (
			(sessionDocumentsStore.currentStep?.label ===
				DocumentType.EnrollmentForm ||
				(sessionDocumentsStore.currentStep?.label ===
					DocumentType.StatementOfGoodsAndServices &&
					sessionStore.activeFh?.billingAddress?.stateCode === 'TX')) &&
			sessionDocumentsStore.currentStep?.completed === false
		) {
			document
				.querySelectorAll('#change-payment-type-btn')
				.forEach((btn) => btn.classList.remove('hidden'))
		} else {
			document
				.querySelectorAll('#change-payment-type-btn')
				.forEach((btn) => btn.classList.add('hidden'))
		}
	}

	// Updates the add custom item button visibility
	const updateAddCustomItemButton = () => {
		if (
			sessionDocumentsStore.currentStep?.label ===
				DocumentType.StatementOfGoodsAndServices &&
			sessionDocumentsStore.currentStep?.completed === false
		) {
			document.querySelector('#add-custom-item-btn')?.classList.remove('hidden')
		} else {
			document.querySelector('#add-custom-item-btn')?.classList.add('hidden')
		}
	}

	const revertAllSignedFieldsToButtons = async () => {
		const pdfViewer = getPdfViewer()
		const viewerDoc = pdfViewer.getCurrentPDFDoc()
		const pages = viewerDoc?.getPageCount()!

		for (let index = 0; index < pages; index++) {
			const page = await viewerDoc?.getPageByIndex(index)
			const annots = (await page!.getAnnots()) as FoxItAnnot[]
			annots.forEach((annot: FoxItAnnot) => {
				revertSignedAnnotationToButton({ annot }, pdfViewer)
			})
		}
	}

	const setFilledFieldsToTransparent = async () => {
		const pdfViewer = getPdfViewer()
		const pdfDoc = pdfViewer.getCurrentPDFDoc()
		const form = (await pdfDoc?.loadPDFForm()) as unknown as FoxItPDFForm

		for (const field of form.fields) {
			// if field is empty, leave the highlight on
			if (field.info.value) {
				setRequiredFieldTransparent(field.info.name)
			}
		}
	}

	// Sets the required field background color to transparent
	const setRequiredFieldTransparent = (fieldName: string) => {
		const domEl = document.querySelector(
			`[fieldname="${fieldName}"] .fv__ui-form-foreground.required`
		) as HTMLElement

		if (domEl) {
			domEl.style.backgroundColor = 'transparent'
		}
	}

	const addCursiveFontToSignatureDialog = async () => {
		const pdfui = getPdfui()
		const typeInput = (await pdfui.getComponentByName(
			'type-cssUrl-input'
		)) as unknown as FoxItComponent
		const typeFontName = (await pdfui.getComponentByName(
			'type-fontFamily'
		)) as unknown as FoxItComponent
		let fontSelectList = await pdfui.getComponentByName('type-font-family')

		// Check if select already includes the Cedarville option
		const fontAlreadyExists: boolean = cursiveFontExists(fontSelectList.element)

		if (!fontAlreadyExists) {
			const addFontButton = await pdfui.getComponentByName(
				'sign-dialog-add-font'
			)
			const fontUrl =
				'https://fonts.googleapis.com/css2?family=Cedarville+Cursive&display=swap'
			typeInput.setValue(fontUrl)
			typeFontName.setValue('Cedarville Cursive')

			addFontButton.element.click()
		}

		setCursiveAsSelected(fontSelectList.element)

		const inkSignDialog = await pdfui.getComponentByName('create-signature')

		inkSignDialog.element
			.querySelector('[component-name="sign-type"]')
			?.dispatchEvent(new Event('click'))
	}

	const turnAllSignaturesToPushButtons = async (pdfForm: FoxItPDFForm) => {
		const sigFields = pdfForm.fields.filter(
			(field: FoxItField) => field.info.type === 7
		)

		await Promise.all([
			...sigFields.map(async (field: FoxItField) => {
				return changeFieldToPushButton(field, pdfForm, field.info.name)
			}),
		])
	}

	const appendUuidToSignatureDateFields = async (pdfForm: FoxItPDFForm) => {
		const sigDateFieldRegex = /Signature\d?Date/i
		const signatureDateFields = pdfForm.fields.filter((field: FoxItField) =>
			sigDateFieldRegex.test(field.info.name)
		)

		await Promise.all([
			...signatureDateFields.map(async (field: FoxItField) => {
				return updateDateFieldName(field, pdfForm, field.info.name)
			}),
		])
	}

	// Grab the existing date field and update its field name
	const updateDateFieldName = async (
		pdfField: FoxItField,
		pdfForm: FoxItPDFForm,
		fieldName: string
	): Promise<void> => {
		const fieldControl = pdfField.getControlByIndex(0)
		const widget = await fieldControl.getWidgetAnnot()
		const pageIndex = widget.getPage().info.index
		const sigRect = widget.getRect()
		const uID = Math.random().toString(36).substring(2, 15)
		await pdfForm.addControl(pageIndex, `${fieldName}_${uID}`, 6, sigRect)
		await pdfForm.removeField(pdfField.info.name)
	}

	const revertSignedAnnotationToButton = async (
		data: { annot: FoxItAnnot },
		pdfViewer: PDFViewCtrl.PDFViewer
	) => {
		if (
			!data.annot.info.name.includes('Signature') &&
			!data.annot.info.name.includes('Initial')
		) {
			return
		}

		const pageIndex = data.annot.getPage().info.index
		const doc = pdfViewer.getCurrentPDFDoc()
		const form = (await doc?.loadPDFForm()) as unknown as FoxItPDFForm
		const { newFieldName } = await form!.addControl(
			pageIndex,
			data.annot.info.name,
			1,
			data.annot.info.rect
		)
		const pushButton = form!.getField(newFieldName) as unknown as FoxItField
		const pushButtonControl = await pushButton.getControlByIndex(0)

		await setPushButtonAppearance(pushButtonControl)
		const currentDocumentType = sessionDocumentsStore.currentDocument?.meta.type
		setSignatureDateField(form, newFieldName, '', currentDocumentType)
		setSignatureRelatedTextField(
			form,
			getNormalizedSignatureFieldName(newFieldName)!,
			'Time',
			''
		)

		await data.annot.page.removeAnnotByObjectNumber(
			data.annot.info.objectNumber
		)
	}

	// Changes a signature field to a push button, happens when the doc is first opened
	// cancellation purchaser signature should not be turned into a button
	const changeFieldToPushButton = async (
		pdfField: FoxItField,
		pdfForm: FoxItPDFForm,
		fieldName: string
	): Promise<void> => {
		if (fieldName === 'cancellationPurchaserSignature') return

		const sigControl = pdfField.getControlByIndex(0)
		const widget = await sigControl.getWidgetAnnot()
		const pageIndex = widget.getPage().info.index
		const sigRect = widget.getRect()
		const uID = Math.random().toString(36).substring(2, 15)
		const { newFieldName } = await pdfForm.addControl(
			pageIndex,
			`${fieldName}_${uID}`,
			1,
			sigRect
		)
		const pushButton = pdfForm.getField(newFieldName) as unknown as FoxItField
		const pushButtonControl = await pushButton.getControlByIndex(0)

		await setPushButtonAppearance(pushButtonControl)
		await pdfForm.removeField(pdfField.info.name)
	}

	// Creates the appearance of the push buttons used in when signature fields are changed to push buttons
	const setPushButtonAppearance = async (
		pushButtonControl: PDFViewCtrl.PDF.form.PDFControl
	) => {
		const pushButtonWidget = await pushButtonControl.getWidgetAnnot()

		await pushButtonControl.setDefaultAppearance({
			textColor: 0xff000000,
			textSize: 8,
			fontName: 'Helvetica',
		})
		pushButtonWidget.setMKProperty('fillColor', 0xffdf00)
		pushButtonWidget.setMKProperty('normalCaption', 'Sign Here')
		pushButtonWidget.setMKProperty('buttonDown', 'true')
	}

	// Shows the signature dropdown when a signature button is clicked
	const showSignatureDropdown = async () => {
		const signDropdown = document.querySelector(
			'[component-name="pdf-sign"]'
		) as HTMLElement

		signDropdown?.click()
	}

	// Shows the signature dialog when a signature button is clicked and no signature is available
	const showSignatureDialog = async () => {
		const pdfui = getPdfui()
		const inkSignDialog = await pdfui.getComponentByName('create-signature')

		inkSignDialog.show()

		await addCursiveFontToSignatureDialog()

		const imageTab = await pdfui.getComponentByName('sign-img-tab')

		if (imageTab) {
			imageTab.destroy()
		}

		const fontBoxLabel = document.querySelector(
			'div[data-i18n="dialog.signDialog.typeTab.customFont"]'
		)
		fontBoxLabel?.parentElement?.remove()

		const fontListBoxLabel = document.querySelector(
			'div[data-i18n="dialog.signDialog.typeTab.fontList"]'
		)
		fontListBoxLabel?.parentElement?.remove()
	}

	// Helper function to whether cursive custom font is already added
	const cursiveFontExists = (fontSelectElement: HTMLElement): boolean => {
		const allOptions: HTMLOptionElement[] = Array.from(
			fontSelectElement.querySelectorAll('option')
		)

		const cedarvilleExists = allOptions.some(
			(option) => option.value === 'Cedarville Cursive'
		)

		return cedarvilleExists
	}

	// Helper function to set Cedarville Cursive as selected
	const setCursiveAsSelected = (fontSelectList: HTMLElement) => {
		const allOptions: HTMLOptionElement[] = Array.from(
			fontSelectList.querySelectorAll('option')
		)
		allOptions.forEach((option) => {
			if (option.value === 'Cedarville Cursive') {
				option.setAttribute('selected', '')
			} else {
				option.removeAttribute('selected')
			}
		})
	}

	return {
		updateAutofillButton,
		updateChangePaymentTypeButton,
		updateAddCustomItemButton,
		revertAllSignedFieldsToButtons,
		setFilledFieldsToTransparent,
		addCursiveFontToSignatureDialog,
		appendUuidToSignatureDateFields,
		turnAllSignaturesToPushButtons,
		revertSignedAnnotationToButton,
		changeFieldToPushButton,
		setPushButtonAppearance,
		showSignatureDropdown,
		showSignatureDialog,
		clearActiveSignature,
		setRequiredFieldTransparent,
	}
}
